import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Alert, Stack, Divider, Tooltip,
  useTheme, alpha, IconButton, Snackbar, Menu, MenuItem, ListItemIcon,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
} from '@mui/material';
import {
  AdminPanelSettings, People, Star, PersonOutline, LockOutlined,
  VerifiedUser, CalendarMonth, Email, Send, MoreVert, WorkspacePremium,
  RemoveCircleOutline, DeleteOutline, Shield, ShieldOutlined, Timer,
  ConfirmationNumber, ContentCopy, Add, MarkEmailRead, MarkEmailUnread,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/apiService';
import { useNavigate } from 'react-router-dom';

const TH = { fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1 };

export default function Admin() {
  const theme = useTheme();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState({});   // { email: true } while in-flight
  const [snack, setSnack] = useState(null);      // { msg, severity }

  // Row action menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  // Grant / remove-time dialog
  const [proDialog, setProDialog] = useState(null); // { email, mode: 'grant' | 'remove' }
  const [proDays, setProDays] = useState('30');
  const [proBusy, setProBusy] = useState(false);

  // Confirm dialog (revoke / delete)
  const [confirm, setConfirm] = useState(null); // { title, body, action: fn }
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Invite generation form
  const [invCount, setInvCount] = useState('5');
  const [invLabel, setInvLabel] = useState('beta');
  const [invDays, setInvDays] = useState('');
  const [invBusy, setInvBusy] = useState(false);

  const loadAll = useCallback(() => {
    return Promise.all([
      adminApi.stats(token).then(res => setData(res.data)),
      adminApi.listInvites(token).then(res => setInvites(res.data.invites || [])).catch(() => {}),
    ]);
  }, [token]);

  const notify = (msg, severity = 'success') => setSnack({ msg, severity });
  const errMsg = (err, fallback) => err?.response?.data?.error || fallback;

  const handleSendTest = async (email) => {
    setSending(s => ({ ...s, [email]: true }));
    try {
      await adminApi.sendTestDigest(token, email);
      notify(`Digest sent to ${email}`);
    } catch (err) {
      notify(errMsg(err, 'Send failed'), 'error');
    } finally {
      setSending(s => ({ ...s, [email]: false }));
    }
  };

  const openMenu = (e, u) => { setMenuAnchor(e.currentTarget); setMenuUser(u); };
  const closeMenu = () => { setMenuAnchor(null); };

  const submitProDialog = async () => {
    if (!proDialog) return;
    setProBusy(true);
    const days = parseInt(proDays, 10) || 0;
    try {
      if (proDialog.mode === 'grant') {
        const res = await adminApi.grantPro(token, proDialog.email, days);
        notify(days > 0
          ? `Granted Pro to ${proDialog.email} — ${res.data.proDaysLeft} days left`
          : `Granted lifetime Pro to ${proDialog.email}`);
      } else {
        const res = await adminApi.removeProTime(token, proDialog.email, days);
        notify(res.data.tier === 'free'
          ? `${proDialog.email} dropped to Free`
          : `Removed ${days} days — ${res.data.proDaysLeft} days left`);
      }
      setProDialog(null);
      await loadAll();
    } catch (err) {
      notify(errMsg(err, 'Update failed'), 'error');
    } finally {
      setProBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      await confirm.action();
      setConfirm(null);
      await loadAll();
    } catch (err) {
      notify(errMsg(err, 'Action failed'), 'error');
    } finally {
      setConfirmBusy(false);
    }
  };

  const toggleAdmin = async (u) => {
    try {
      await adminApi.setAdmin(token, u.email, !u.isAdmin);
      notify(`${u.email} is ${!u.isAdmin ? 'now an admin' : 'no longer an admin'}`);
      await loadAll();
    } catch (err) {
      notify(errMsg(err, 'Update failed'), 'error');
    }
  };

  const generateInvites = async () => {
    setInvBusy(true);
    try {
      const res = await adminApi.createInvites(token, {
        count: parseInt(invCount, 10) || 1,
        label: invLabel || 'admin-ui',
        days: parseInt(invDays, 10) || 0,
      });
      notify(`Generated ${res.data.codes.length} invite code(s)`);
      await loadAll();
    } catch (err) {
      notify(errMsg(err, 'Generation failed'), 'error');
    } finally {
      setInvBusy(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(() => notify(`Copied ${code}`)).catch(() => {});
  };

  const deleteInvite = async (code) => {
    try {
      await adminApi.deleteInvite(token, code);
      notify(`Deleted ${code}`);
      await loadAll();
    } catch (err) {
      notify(errMsg(err, 'Delete failed'), 'error');
    }
  };

  useEffect(() => {
    if (authLoading) return; // wait for auth to hydrate
    if (!user || !user.isAdmin) { navigate('/'); return; }
    loadAll()
      .catch(err => setError(err.response?.data?.error || 'Failed to load admin stats'))
      .finally(() => setLoading(false));
  }, [user, token, authLoading, navigate, loadAll]);

  if (authLoading || loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">{error}</Alert>
    </Box>
  );

  const { summary, signupsByDay, users } = data || {};

  // Signups chart — last 30 days sorted
  const chartData = Object.entries(signupsByDay || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-30)
    .map(([date, count]) => ({ date: date.slice(5), count })); // MM-DD

  const statCards = [
    { label: 'Total Users', value: summary?.total ?? 0, icon: <People />, color: theme.palette.primary.main },
    { label: 'Pro (Paid)', value: summary?.paid ?? 0, icon: <Star />, color: theme.palette.warning.main },
    { label: 'Free', value: summary?.free ?? 0, icon: <PersonOutline />, color: theme.palette.success.main },
    { label: 'Admins', value: summary?.admins ?? 0, icon: <AdminPanelSettings />, color: theme.palette.error.main },
  ];

  const proTimeLabel = (u) => {
    if (u.tier !== 'paid') return '—';
    if (u.hasStripe) return 'Stripe';
    if (u.proDaysLeft != null) return `${u.proDaysLeft}d left`;
    return '∞';
  };

  const unusedInvites = invites.filter(i => !i.usedBy);
  const usedInvites = invites.filter(i => i.usedBy);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <AdminPanelSettings sx={{ fontSize: 32, color: theme.palette.primary.main }} />
        <Box>
          <Typography variant="h5" fontWeight={800}>Admin Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">thecycle.online user analytics</Typography>
        </Box>
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((s) => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid ' + alpha(s.color, 0.2), bgcolor: alpha(s.color, 0.04) }}>
              <CardContent sx={{ py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Box sx={{ color: s.color, display: 'flex' }}>{s.icon}</Box>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">{s.label}</Typography>
                </Stack>
                <Typography variant="h4" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Signups chart */}
      {chartData.length > 0 && (
        <Card elevation={0} sx={{ mb: 3, borderRadius: 2, border: '1px solid ' + alpha(theme.palette.divider, 0.15) }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <CalendarMonth sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" fontWeight={700}>Signups by Day</Typography>
            </Stack>
            <Box sx={{ height: 160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ left: -20, right: 10, top: 5, bottom: 5 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} allowDecimals={false} />
                  <RTooltip
                    contentStyle={{ background: theme.palette.background.paper, border: '1px solid ' + theme.palette.divider, borderRadius: 8, fontSize: 12 }}
                    cursor={{ fill: alpha(theme.palette.primary.main, 0.1) }}
                  />
                  <Bar dataKey="count" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} name="Signups" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Users table */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 2, border: '1px solid ' + alpha(theme.palette.divider, 0.15) }}>
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <People sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" fontWeight={700}>All Users</Typography>
            <Chip label={users?.length ?? 0} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.72rem' }} />
          </Stack>
        </CardContent>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                <TableCell sx={TH}>Email</TableCell>
                <TableCell sx={TH} align="center">Tier</TableCell>
                <TableCell sx={TH} align="center">Pro Time</TableCell>
                <TableCell sx={TH} align="center">Stripe</TableCell>
                <TableCell sx={TH} align="center">Verified</TableCell>
                <TableCell sx={TH} align="center">Admin</TableCell>
                <TableCell sx={TH}>Joined</TableCell>
                <TableCell sx={TH} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(users || []).map((u) => (
                <TableRow key={u.email} hover sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Email sx={{ fontSize: 14, color: theme.palette.text.disabled }} />
                      <Typography variant="caption" fontWeight={600}>{u.email}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={u.tier === 'paid' ? (u.isInviteCode ? 'Pro (Invite)' : 'Pro') : 'Free'}
                      size="small"
                      color={u.tier === 'paid' ? 'warning' : 'default'}
                      sx={{ fontWeight: 700, height: 20, fontSize: '0.72rem' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                      {u.tier === 'paid' && !u.hasStripe && u.proDaysLeft != null && <Timer sx={{ fontSize: 13, color: theme.palette.text.disabled }} />}
                      <Typography variant="caption" fontWeight={600} color={u.tier === 'paid' ? 'text.primary' : 'text.disabled'}>
                        {proTimeLabel(u)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    {u.hasStripe
                      ? <Tooltip title="Has Stripe customer"><VerifiedUser sx={{ fontSize: 16, color: theme.palette.success.main }} /></Tooltip>
                      : <Tooltip title="No Stripe customer"><LockOutlined sx={{ fontSize: 16, color: theme.palette.text.disabled }} /></Tooltip>}
                  </TableCell>
                  <TableCell align="center">
                    {u.emailVerified
                      ? <Tooltip title="Email verified"><MarkEmailRead sx={{ fontSize: 16, color: theme.palette.success.main }} /></Tooltip>
                      : <Tooltip title="Email NOT verified"><MarkEmailUnread sx={{ fontSize: 16, color: theme.palette.warning.main }} /></Tooltip>}
                  </TableCell>
                  <TableCell align="center">
                    {u.isAdmin && <AdminPanelSettings sx={{ fontSize: 16, color: theme.palette.error.main }} />}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={(e) => openMenu(e, u)}>
                      <MoreVert sx={{ fontSize: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(!users || users.length === 0) && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Invite codes */}
      <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid ' + alpha(theme.palette.divider, 0.15) }}>
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <ConfirmationNumber sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" fontWeight={700}>Invite Codes</Typography>
            <Chip label={`${unusedInvites.length} unused`} size="small" color="success" sx={{ fontWeight: 700, height: 20, fontSize: '0.72rem' }} />
            <Chip label={`${usedInvites.length} used`} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.72rem' }} />
          </Stack>

          {/* Generate form */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <TextField label="Count" size="small" value={invCount} onChange={e => setInvCount(e.target.value)} sx={{ width: { sm: 90 } }} inputProps={{ inputMode: 'numeric' }} />
            <TextField label="Label" size="small" value={invLabel} onChange={e => setInvLabel(e.target.value)} sx={{ width: { sm: 160 } }} />
            <TextField label="Days (blank = lifetime)" size="small" value={invDays} onChange={e => setInvDays(e.target.value)} sx={{ width: { sm: 190 } }} inputProps={{ inputMode: 'numeric' }} />
            <Button
              variant="contained" size="small" startIcon={invBusy ? <CircularProgress size={14} color="inherit" /> : <Add />}
              disabled={invBusy} onClick={generateInvites}
              sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
            >
              Generate
            </Button>
          </Stack>
        </CardContent>
        <Divider />
        <TableContainer sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={TH}>Code</TableCell>
                <TableCell sx={TH}>Label</TableCell>
                <TableCell sx={TH} align="center">Days</TableCell>
                <TableCell sx={TH}>Used By</TableCell>
                <TableCell sx={TH}>Created</TableCell>
                <TableCell sx={TH} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.map((inv) => (
                <TableRow key={inv.code} hover>
                  <TableCell>
                    <Typography variant="caption" fontWeight={700} sx={{ fontFamily: 'monospace', color: inv.usedBy ? 'text.disabled' : 'text.primary', textDecoration: inv.usedBy ? 'line-through' : 'none' }}>
                      {inv.code}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{inv.label || '—'}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="caption">{inv.days ? `${inv.days}d` : '∞'}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color={inv.usedBy ? 'text.primary' : 'text.disabled'}>{inv.usedBy || 'unused'}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—'}</Typography></TableCell>
                  <TableCell align="center">
                    <Tooltip title="Copy code">
                      <IconButton size="small" onClick={() => copyCode(inv.code)}>
                        <ContentCopy sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    {!inv.usedBy && (
                      <Tooltip title="Delete code">
                        <IconButton size="small" color="error" onClick={() => deleteInvite(inv.code)}>
                          <DeleteOutline sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {invites.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">No invite codes yet</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Per-user action menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuUser && [
          <MenuItem key="grant" onClick={() => { const u = menuUser; closeMenu(); setProDays('30'); setProDialog({ email: u.email, mode: 'grant' }); }}>
            <ListItemIcon><WorkspacePremium fontSize="small" sx={{ color: 'warning.main' }} /></ListItemIcon>
            {menuUser.tier === 'paid' ? 'Extend / modify Pro' : 'Grant Pro'}
          </MenuItem>,
          menuUser.tier === 'paid' && !menuUser.hasStripe && menuUser.proDaysLeft != null && (
            <MenuItem key="removetime" onClick={() => { const u = menuUser; closeMenu(); setProDays('7'); setProDialog({ email: u.email, mode: 'remove' }); }}>
              <ListItemIcon><Timer fontSize="small" /></ListItemIcon>
              Remove Pro time
            </MenuItem>
          ),
          menuUser.tier === 'paid' && !menuUser.hasStripe && (
            <MenuItem key="revoke" onClick={() => {
              const u = menuUser; closeMenu();
              setConfirm({
                title: 'Revoke Pro',
                body: `Remove Pro access from ${u.email} immediately?`,
                action: async () => { await adminApi.revokePro(token, u.email); notify(`Revoked Pro from ${u.email}`); },
              });
            }}>
              <ListItemIcon><RemoveCircleOutline fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              Revoke Pro
            </MenuItem>
          ),
          <Divider key="d1" />,
          <MenuItem key="admin" onClick={() => { const u = menuUser; closeMenu(); toggleAdmin(u); }}>
            <ListItemIcon>{menuUser.isAdmin ? <ShieldOutlined fontSize="small" /> : <Shield fontSize="small" sx={{ color: 'error.main' }} />}</ListItemIcon>
            {menuUser.isAdmin ? 'Remove admin' : 'Make admin'}
          </MenuItem>,
          <MenuItem key="digest" disabled={!!sending[menuUser.email]} onClick={() => { const u = menuUser; closeMenu(); handleSendTest(u.email); }}>
            <ListItemIcon><Send fontSize="small" /></ListItemIcon>
            Send test digest
          </MenuItem>,
          <Divider key="d2" />,
          <MenuItem key="delete" sx={{ color: 'error.main' }} onClick={() => {
            const u = menuUser; closeMenu();
            setConfirm({
              title: 'Delete user',
              body: `Permanently delete ${u.email}? This cannot be undone.`,
              action: async () => { await adminApi.deleteUser(token, u.email); notify(`Deleted ${u.email}`); },
            });
          }}>
            <ListItemIcon><DeleteOutline fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
            Delete user
          </MenuItem>,
        ].filter(Boolean)}
      </Menu>

      {/* Grant / remove Pro time dialog */}
      <Dialog open={!!proDialog} onClose={() => !proBusy && setProDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {proDialog?.mode === 'grant' ? 'Grant Pro time' : 'Remove Pro time'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {proDialog?.mode === 'grant'
              ? `Add Pro days to ${proDialog?.email}. Enter 0 for lifetime access.`
              : `Subtract Pro days from ${proDialog?.email}. If the balance hits zero they drop to Free.`}
          </Typography>
          <TextField
            autoFocus fullWidth size="small" label="Days" value={proDays}
            onChange={e => setProDays(e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProDialog(null)} disabled={proBusy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={submitProDialog} disabled={proBusy} sx={{ fontWeight: 700, textTransform: 'none' }}>
            {proBusy ? <CircularProgress size={18} color="inherit" /> : (proDialog?.mode === 'grant' ? 'Grant' : 'Remove')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onClose={() => !confirmBusy && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{confirm?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirm?.body}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={confirmBusy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={runConfirm} disabled={confirmBusy} sx={{ fontWeight: 700, textTransform: 'none' }}>
            {confirmBusy ? <CircularProgress size={18} color="inherit" /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack?.severity || 'info'} onClose={() => setSnack(null)} sx={{ fontWeight: 600 }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
